import { useState } from "react";
import { apiFetch } from "../utils/api";

export function useGameActions({
  id,
  userId,
  hasJoined,
  gameState,
  setGameState,
  config,
  navigate,
  isRoomStarted,
  isRoomCreator,
  isRoomLobby,
  setFoundingNation,
  setBuildingStructure,
  setIsDefeated,
  setIsSpectating,
  setActionModal,
  setHasFounded,
  setHasJoined,
  setUserState,
  setError,
  setUiMode,
  setPaused,
}) {
  const [isStartingRoom, setIsStartingRoom] = useState(false);

  const handleFoundNation = async (x, y) => {
    if (!userId || !hasJoined) return;

    try {
      const response = await apiFetch(`api/gamerooms/${id}/foundNation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ x, y }),
      });

      if (!response.ok) {
        const errData = await response.json();
        if (errData?.code === "REFOUND_DISABLED") {
          setIsSpectating(true);
          setFoundingNation(false);
        }
        throw new Error(errData.error || "Failed to found nation");
      }

      setFoundingNation(false);
      setIsDefeated(false);
      setIsSpectating(false);
      setActionModal(null);
      setHasFounded(true);
      setUiMode("idle");
    } catch (err) {
      setError(err.message);
      setFoundingNation(false);
      setUiMode("idle");
    }
  };

  const handleBuildCity = async (x, y, cityType, cityName) => {
    // If x and y are null, it means we're just selecting what to build
    if (x === null && y === null) {
      if (!isRoomStarted) return;
      setBuildingStructure(cityType);
      setUiMode("buildStructure");
      return;
    }

    // Otherwise, we're actually building at the selected location
    if (!userId || !hasJoined) return;
    if (!isRoomStarted) return;

    try {
      const response = await apiFetch(`api/gamerooms/${id}/buildCity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          x,
          y,
          cityType,
          cityName,
        }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to build structure");
      }
      // Clear building mode after successful build
      setBuildingStructure(null);
      setUiMode("idle");
    } catch (err) {
      setError(err.message);
      // Also clear building mode on error
      setBuildingStructure(null);
      setUiMode("idle");
    }
  };

  const handlePauseGame = async () => {
    try {
      const response = await apiFetch(`api/gamerooms/${id}/pause`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error("Failed to pause game");
      }
      const data = await response.json();
      console.log("Game paused:", data);
      setPaused(true);
    } catch (err) {
      console.error("Error pausing game:", err);
    }
  };

  const handleUnPauseGame = async () => {
    try {
      const response = await apiFetch(`api/gamerooms/${id}/unpause`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error("Failed to unpause game");
      }
      const data = await response.json();
      console.log("Game unpaused:", data);
      setPaused(false);
    } catch (err) {
      console.error("Error unpausing game:", err);
    }
  };

  const handleEndGame = async () => {
    try {
      const response = await apiFetch(`api/gamerooms/${id}/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error("Failed to end game");
      }
      const data = await response.json();
      console.log("Game ended:", data);
    } catch (err) {
      console.error("Error ending game:", err);
    }
  };

  const handleQuitGame = async () => {
    try {
      const response = await apiFetch(`api/gamerooms/${id}/quit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error("Failed to quit game");
      }
      setHasJoined(false);
      setFoundingNation(true);
      setHasFounded(false);
      setActionModal(null);
      setUserState(null);
      setGameState(null);
      navigate("/rooms");
    } catch (err) {
      console.error("Error ending game:", err);
    }
  };

  const handleSetTroopTarget = async (newTarget, setTroopTarget) => {
    if (!isRoomStarted) return;
    setTroopTarget(newTarget);
    if (!userId || !hasJoined) return;
    try {
      await apiFetch(`api/gamerooms/${id}/troopTarget`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          troopTarget: newTarget,
        }),
      });
    } catch (err) {
      console.error("Failed to set troop target:", err);
    }
  };

  const handleStartRoom = async () => {
    if (!isRoomCreator || !isRoomLobby || isStartingRoom) return;
    try {
      setIsStartingRoom(true);
      const response = await apiFetch(`api/gamerooms/${id}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to start room");
      }
      setGameState((prev) =>
        prev
          ? {
              ...prev,
              roomStatus: "open",
            }
          : prev
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setIsStartingRoom(false);
    }
  };

  return {
    handleFoundNation,
    handleBuildCity,
    handlePauseGame,
    handleUnPauseGame,
    handleEndGame,
    handleQuitGame,
    handleSetTroopTarget,
    handleStartRoom,
    isStartingRoom,
  };
}
